import {
  IonIcon,
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { chevronForward, personAddOutline, stopwatchOutline } from "ionicons/icons";
import { FC, ReactElement, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import styled, { css } from "styled-components";
import Card from "../../components/atoms/Card";
import Info from "../../components/atoms/ErrorComponent";
import Icon from "../../components/atoms/Icon";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import SmallButton from "../../components/atoms/smallButton";
import { useConfig } from "../../contexts/ConfigContext";
import { ComponentType } from "../../types/Questionnaire";
import { RoleEnum } from "../../types/invitation.types";
import { getStudyTabIds } from "../../navigation/studyTabs";
import dayjs from "dayjs";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";

const SequenceComponents: FC = (): ReactElement => {
  const history = useHistory();
  const { study_slug } = useParams<{ study_slug: string }>();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "Questionnaires",
      welcomePage: false,
    });
  });

  const { sequences, loading, error } = useResonanceData();
  const { selectedRole } = useConfig();
  // A subject viewing their own family's occurrences: the numéro de dossier is their own —
  // redundant chrome. Staff (investigator/intervenor) keep it to tell families apart.
  const isSubjectViewer = selectedRole?.name === RoleEnum.SUBJECT;

  const onComponentClick = (sequence_id: number, component_id: number) => {
    console.log(
      "[onComponentClick] navigating to sequence:",
      sequence_id,
      "component:",
      component_id
    );
    ionRouter.push(
      `/${study_slug}/tabs/questionnaires/sequences/${sequence_id}/components/${component_id}`,
      "forward",
      "push"
    );
  };

  const questionnaires = sequences
    .filter((seq) => seq.status !== "DONE") // ignore finished sequences
    .flatMap((seq) =>
      seq.components
        .filter((c) => c.questionnaire_id) // keep only questionnaire components
        .map((c) => ({
          ...c,
          sequence_id: seq.id, // keep link to parent sequence
          sequenceLabel: seq.label,
          sequenceStatus: seq.status,
          // Subject context for about-someone-else occurrences (investigator fiches,
          // a parent's per-child questionnaires) so cards about different subjects are
          // distinguishable. subjectDisplayLabel is the API's best label («Emma» /
          // «Enfant 2»); older payloads fall back to the plain role label. The backend
          // nulls these fields when the user answers about themselves, so their
          // presence is the "is about a subject" signal. Case id (= external_id /
          // numéro de dossier, shared by a family) stays staff-only.
          subjectRoleLabel: seq.subjectDisplayLabel ?? seq.subjectRoleLabel ?? null,
          subjectCaseId:
            !isSubjectViewer &&
            (seq.subjectDisplayLabel || seq.subjectInfo || seq.subjectRoleLabel)
              ? seq.subject?.external_id ?? null
              : null,
        }))
    )
    .filter((q) => !q.is_done) // keep only unfinished questionnaires
    .sort(
      (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
    );

  const isInitialLoading = loading && questionnaires == null; // null or undefined
  const isEmpty = !loading && (questionnaires?.length ?? 0) === 0;

  // The invitation tab is subject-role-gated and per-study opt-in; only nudge toward it
  // when this study/role actually has it (Outborn subjects). The invitation page itself
  // still fail-softs when no config is sendable.
  const invitationAvailable = getStudyTabIds(
    study_slug,
    selectedRole?.name
  ).includes("invitation");

  if (error) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );
  }

  return (
    <ResonanceLayout isConnected>
      <>
      {isInitialLoading && <ResonanceLoader />}

      {!isInitialLoading && isEmpty && (
        <EmptyState>
          <EmptyMessage>{t("sequence_empty_list")}</EmptyMessage>
          {invitationAvailable && (
            <InviteCta
              type="button"
              onClick={() =>
                ionRouter.push(
                  `/${study_slug}/tabs/invitation`,
                  "forward",
                  "push"
                )
              }
            >
              <InviteCtaBadge>
                <IonIcon icon={personAddOutline} />
              </InviteCtaBadge>
              <InviteCtaText>
                <InviteCtaTitle>{t("invite_cta_title")}</InviteCtaTitle>
                <InviteCtaSubtitle>{t("invite_cta_subtitle")}</InviteCtaSubtitle>
              </InviteCtaText>
              <IonIcon icon={chevronForward} className="invite-cta-chevron" />
            </InviteCta>
          )}
        </EmptyState>
      )}

      {!isInitialLoading && !isEmpty && (
        <>
          {(questionnaires ?? []).map((q) => {
            const Renderer = q.questionnaire_id
              ? QuestionnaireGamified
              : ContentGamified;
            return (
              <Renderer
                key={q.id}
                questionnaire={q}
                onComponentClick={onComponentClick}
              />
            );
          })}
          <Blank />
        </>
      )}
      </>
    </ResonanceLayout>
  );
};

interface PropsGamified {
  questionnaire: ComponentType & {
    subjectRoleLabel?: string | null;
    subjectCaseId?: string | null;
  };
  onComponentClick(s: number, c: number): void;
}

const QuestionnaireGamified: FC<PropsGamified> = ({
  questionnaire: questionnaire,
  onComponentClick,
}) => {
  const { study } = useConfig();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <QuestionnaireCard
      onClick={() => {
        console.log(
          "[QuestionnaireGamified] Card clicked:",
          questionnaire.sequence_id,
          questionnaire.id
        );
        onComponentClick(questionnaire.sequence_id, questionnaire.id);
      }}
    >
      {(questionnaire.subjectRoleLabel || questionnaire.subjectCaseId) && (
        <SubjectLine>
          {questionnaire.subjectRoleLabel && (
            <SubjectRole>{questionnaire.subjectRoleLabel}</SubjectRole>
          )}
          {questionnaire.subjectCaseId && (
            <SubjectCaseId>{questionnaire.subjectCaseId}</SubjectCaseId>
          )}
        </SubjectLine>
      )}
      <QuestionnaireTitle>
        {questionnaire.questionnaire_title}
      </QuestionnaireTitle>
      {/* Conditionally render the image only if the study is gamified */}
      {/*study.is_gamified && <QuestionnaireImage src={imageSrc} />*/}
      <QuestionnaireIcons>
        <QuestionnaireIcon src="/assets/icon/doc.svg" />
        <QuestionnaireIconLabel>
          {questionnaire.questions_count} {t("questionnaire_questionnaire")}
        </QuestionnaireIconLabel>
        <QuestionnaireIcon src={stopwatchOutline} />
        <QuestionnaireIconLabel>
          {questionnaire.avg_amount_time_to_answer} {t("questionnaire_minutes")}
        </QuestionnaireIconLabel>
      </QuestionnaireIcons>

      {/* Conditionally render points only if the study is gamified */}
      {study.is_gamified && (
        <>
          <br />
          <QuestionnaireIcons>
            <QuestionnaireIcon src="/assets/icon/gift.svg" />
            <QuestionnaireIconLabel>
              {questionnaire.points} {t("questionnaire_point")}
            </QuestionnaireIconLabel>
          </QuestionnaireIcons>
        </>
      )}

      <QuestionnaireMeta>
        <div>
          Deadline: {dayjs(questionnaire.deadline).format("DD/MM/YYYY")}
        </div>
        <QuestionnaireButton>
          {questionnaire.answered_questions_count > 0
            ? String(t("questionnaire_continue"))
            : String(t("questionnaire_start"))}
        </QuestionnaireButton>
      </QuestionnaireMeta>

      <ProgressContainer>
        {/* <ProgressLabel>
    {Math.round((questionnaire.answered_questions_count / questionnaire.questions_count) * 100)}%
  </ProgressLabel> */}
        <ProgressBarWrapper>
          <ProgressFill
            // questions_count can be 0 (e.g. an information-only questionnaire —
            // answerless pages are excluded from the denominator): 0/0 is NaN.
            $width={
              questionnaire.questions_count > 0
                ? Math.min(
                    100,
                    (questionnaire.answered_questions_count /
                      questionnaire.questions_count) *
                      100
                  )
                : 0
            }
          />
        </ProgressBarWrapper>
      </ProgressContainer>
    </QuestionnaireCard>
  );
};

const ContentGamified: FC<PropsGamified> = ({
  questionnaire: questionnaire,
  onComponentClick,
}) => {
  return (
    <ComponentCard
      as="div"
      onClick={() => {
        console.log(
          "[ContentGamified] Card clicked:",
          questionnaire.content_id,
          questionnaire.questionnaire_id
        );
        onComponentClick(
          questionnaire.content_id,
          questionnaire.questionnaire_id
        );
      }}
    >
      <div>
        <ComponentTitle>{questionnaire.content_label}</ComponentTitle>
      </div>

      <ComponentIcon icon={chevronForward} />
    </ComponentCard>
  );
};

export default SequenceComponents;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  min-height: 60vh;
  padding: 24px 20px;
  text-align: center;
`;

const EmptyMessage = styled.div`
  font-size: 19px;
  line-height: 1.45;
  font-weight: 500;
  max-width: 420px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
`;

const InviteCta = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 440px;
  padding: 16px 18px;
  border-radius: 18px;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  background: var(--app-color-surface, #fff);
  border: 1px solid rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.22);
  box-shadow: 0 6px 20px rgba(var(--app-color-text-rgb, 15, 23, 42), 0.07);
  transition: transform 0.05s ease;
  &:active {
    transform: scale(0.99);
  }
  .invite-cta-chevron {
    font-size: 20px;
    color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.4);
    flex-shrink: 0;
  }
`;

const InviteCtaBadge = styled.div`
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.1);
  color: var(--app-color-primary, #2563eb);
  ion-icon {
    font-size: 24px;
  }
`;

const InviteCtaText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const InviteCtaTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--app-color-text, #0f172a);
`;

const InviteCtaSubtitle = styled.div`
  font-size: 13.5px;
  line-height: 1.4;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.55);
`;

/**
 * Desktop reading column for the cards.
 *
 * The cards render as direct children of IonContent with no wrapper, so they
 * inherit its 1200px cap and stretch to ~1160px on a laptop — a one-line
 * questionnaire title spread across the whole screen.
 *
 * Centred with AUTO HORIZONTAL MARGINS, because this is a BLOCK formatting
 * context. IonContent's `display: flex` sits on the host element, where it
 * governs the shadow-DOM children; slotted light-DOM children like these cards
 * are laid out inside `.inner-scroll`, which Ionic gives `position: absolute`
 * and no `display` — i.e. block. So `align-self` does nothing here, and an
 * explicit side margin pins the card to the left instead of centring it.
 */
const desktopCardColumn = css`
  @media (min-width: 768px) {
    width: 100%;
    max-width: 620px;
    /* max-width resolves before margins, so 620px leaves free space for the
       autos to split evenly. */
    margin: 16px auto;
  }
`;

const ComponentCard = styled(Card)`
  padding: 20px 10px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  ${desktopCardColumn}
`;

const QuestionnaireCard = styled(Card)<React.HTMLAttributes<HTMLDivElement>>`
  padding: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  ${desktopCardColumn}
`;

const Blank = styled.div`
  width: 100%;
  height: 50px;
`;

const ProgressContainer = styled.div`
  width: 100%;
  margin: 10px 0;

  @media (min-width: 768px) {
    margin: 6px 0;
  }
`;

const ProgressBarWrapper = styled.div`
  background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  border-radius: 10px;
  height: 10px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $width?: number }>`
  /* Progress fills sweep from the emphasis tone (strong, derived from
     primary) at the leading edge to its pale -soft end — the visible
     gradient the historical bar had. */
  background: linear-gradient(
    270deg,
    var(--app-color-emphasis, var(--app-color-primary, #2db6b4)) 0%,
    var(--app-color-emphasis-soft, var(--app-color-primary-tint, #abf0ef)) 105%
  );
  height: 100%;
  border-radius: 10px;
  width: ${(props) => props.$width}%;
  transition: width 0.4s ease-in-out;
`;

const SubjectLine = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const SubjectRole = styled.span`
  font-weight: 700;
  color: ${(p) => p.theme.primary.main};
`;

const SubjectCaseId = styled.span`
  color: #666;
`;

const QuestionnaireTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 30px;

  /* The 30px gap under the title is most of the card's height on desktop, where
     the card is no longer a full-screen phone panel that needs the breathing room. */
  @media (min-width: 768px) {
    font-size: 18px;
    margin-bottom: 14px;
  }
`;

const QuestionnaireButton = styled(SmallButton)`
  background-color: ${(p) => p.theme.secondary.main};
  color: ${(p) => p.theme.secondary.contrast};
  font-weight: 600;
`;

const QuestionnaireIcons = styled.div`
  display: flex;
  justify-content: row;
  align-items: center;
  width: 100%;
`;

const QuestionnaireIconLabel = styled.span`
  margin-left: 5px;
  margin-right: 10px;
`;
const QuestionnaireIcon = styled(Icon)`
  color: ${(p) => p.theme.primary.main};
  font-size: 25px;
  padding: 0;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const QuestionnaireMeta = styled.div`
  display: flex;
  justify-content: row;
  width: 100%;
  justify-content: space-between;
  margin: 10px 0 0px;

  @media (min-width: 768px) {
    margin-top: 6px;
  }
`;

const ComponentTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`;

const ComponentIcon = styled(Icon)`
  color: #929292;
  font-size: 20px;
`;
